import React, { useState } from 'react'
import { Button } from 'react-bootstrap'
import { Bot } from 'lucide-react'
import { AnnotationDataBoundingBox } from '../../../types/models/annotation'
import { useAppSelector } from '../../../store/hooks'
import { selectSelectedProjectId } from '../../../store/slices/projectSlice'
import { useGetProjectQuery } from '../../../services/projectsApi'
import { useGetTagsQuery } from '../../../services/tagApi'
import { Tag } from '../../../types/models/tag'
import AALSnippet from '../../annotation/AALSnippet'

interface AALReviewSnippetCardProps {
  contentId: string
  bbox: AnnotationDataBoundingBox
  isSelected: boolean
  showConfidence?: boolean
  showAcceptButton?: boolean
  showRejectButton?: boolean
  onAccept?: () => void
  onReject?: () => void
  onClick?: () => void
  onHover?: () => void
  onHoverEnd?: () => void
  rotateLongSideVertical?: boolean
}

const AALReviewSnippetCard: React.FC<AALReviewSnippetCardProps> = ({
  contentId,
  bbox,
  isSelected,
  showConfidence = true,
  showAcceptButton = false,
  showRejectButton = false,
  onAccept,
  onReject,
  onClick,
  onHover,
  onHoverEnd,
  rotateLongSideVertical = false,
}) => {
  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const { data: project } = useGetProjectQuery(selectedProjectId || '', {
    skip: !selectedProjectId,
  })

  const { data: tags } = useGetTagsQuery(
    {
      project_id: selectedProjectId || '',
      dataset_id: project?.datasetid || '',
    },
    { skip: !selectedProjectId || !project?.datasetid }
  )

  const [isHovered, setIsHovered] = useState(false)

  const tag = tags?.tags?.find((t: Tag) => t.id === bbox.name || t.id === bbox.class_name)
  const tagName = tag?.name || bbox.class_name || 'Unknown'

  const handleMouseEnter = () => {
    setIsHovered(true)
    onHover?.()
  }

  const handleMouseLeave = () => {
    setIsHovered(false)
    onHoverEnd?.()
  }

  return (
    <div
      onClick={onClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{
        cursor: 'pointer',
        borderRadius: '8px',
        overflow: 'hidden',
        transition: 'all 0.2s ease',
        border: isSelected
          ? '2px solid #58a6ff'
          : isHovered
          ? '1px solid rgba(88, 166, 255, 0.5)'
          : '1px solid rgba(255, 255, 255, 0.1)',
        backgroundColor: '#0d1117',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Snippet Thumbnail - Cropped to bounding box */}
      <div
        style={{
          width: '100%',
          aspectRatio: '1',
          backgroundColor: '#0d1117',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <AALSnippet
          contentId={contentId}
          bbox={bbox}
          rotateLongSideVertical={rotateLongSideVertical}
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            width: 'auto',
            height: 'auto',
          }}
        />
      </div>

      {/* Info Section - Compact */}
      <div className="p-2 d-flex flex-column" style={{ flexShrink: 0 }}>
        <div className="d-flex align-items-center gap-1 mb-1">
          <span className="text-light fw-semibold text-truncate" style={{ fontSize: '0.8rem' }}>
            {tagName}
          </span>
          {bbox.ai_labeled && <Bot size={12} className="text-info flex-shrink-0" />}
        </div>

        {showConfidence && bbox.confidence !== undefined && (
          <div className="mb-2" style={{ fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.7)' }}>
            {Math.round(bbox.confidence * 100)}% confidence
          </div>
        )}

        {/* Actions */}
        {(showAcceptButton || showRejectButton) && (
          <div className="d-flex gap-1">
            {showAcceptButton && onAccept && (
              <Button
                variant="outline-success"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation()
                  onAccept()
                }}
                className="flex-grow-1"
                style={{ fontSize: '0.7rem', padding: '0.25rem 0.5rem' }}
              >
                ✓ Accept
              </Button>
            )}
            {showRejectButton && onReject && (
              <Button
                variant="outline-danger"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation()
                  onReject()
                }}
                className="flex-grow-1"
                style={{ fontSize: '0.7rem', padding: '0.25rem 0.5rem' }}
              >
                ✗ Reject
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default AALReviewSnippetCard
